import type { EncounterTiming } from "@allergy123/contracts/clinical";
import { Button, Card, Status } from "@allergy123/ui";
import { useEffect, useState, useSyncExternalStore } from "react";
import { formatTime } from "../lib/format";
import { useSession } from "../session";
import { notifyState, requestNotify, unlockAudio, type NotifyState } from "./cues";
import { clinicTimers } from "./RunningTimers";
import { mmss, notificationFor, type ReadKey, type TimerReadSpec } from "./timers";

/**
 * The test timers (Q-13, C-44): the application time is persisted on the draft the moment the timer starts, and
 * the two reads are due at the panel's intervals. The countdown is always computed from the saved time.
 *
 * The "Start timer" click does, before any await, everything a browser allows only inside a user's gesture: it asks
 * for notification permission (WebKit refuses the request after a network round trip) and unlocks the audio for the
 * chime. Only then is the start saved; the permission prompt never delays it. Once saved, the timer joins the
 * running-timers bar (RunningTimers.tsx), which cues each read on every screen of this tab and holds the idle
 * sign-out. A saved timer this tab is not tracking (started in another tab, or before a reload) can join the bar
 * with "Track this timer here", a click for the same reasons. Cues outside the page carry no patient information
 * (AGENTS.md), and the screen-reader status speaks only when a read comes due, not every second.
 */
export function TimerStep({ encounterId, label, timing, histamineMinutes, fullMinutes, timezone, disabled, onStart }: { encounterId: string; label: string; timing: EncounterTiming; histamineMinutes: number; fullMinutes: number; timezone: string; disabled: boolean; onStart: () => Promise<string | null> }) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState<NotifyState>(notifyState);
  const { organizationId } = useSession();
  const tracked = useSyncExternalStore(clinicTimers.subscribe, () => clinicTimers.get(encounterId) !== undefined);
  const applied = timing.appliedAt ? new Date(timing.appliedAt).getTime() : null;

  useEffect(() => {
    if (!applied) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [applied]);

  // A timer started elsewhere (another tab, before a reload) is not on this tab's bar: notify while this step is open,
  // as before. A timer on the bar is cued by the bar's store on every screen.
  useEffect(() => {
    if (!applied || tracked || notify !== "granted") return;
    const reads: [ReadKey, number][] = [
      ["histamine", histamineMinutes],
      ["full", fullMinutes],
    ];
    const handles = reads.map(([key, minutes]) => {
      const delay = applied + minutes * 60_000 - Date.now();
      if (delay <= 0) return undefined;
      return setTimeout(() => {
        const { title, body } = notificationFor({ key, minutes });
        try {
          new Notification(title, { body, tag: `a123-timer-step-${minutes}` });
        } catch {
          /* notifications only from a service worker here */
        }
      }, delay);
    });
    return () => handles.forEach((h) => h && clearTimeout(h));
  }, [applied, tracked, notify, histamineMinutes, fullMinutes]);

  const remaining = (minutes: number) => (applied ? Math.max(0, applied + minutes * 60_000 - now) : minutes * 60_000);
  const reads: TimerReadSpec[] = [
    { key: "histamine", minutes: histamineMinutes },
    { key: "full", minutes: fullMinutes },
  ];
  const pending = applied !== null && reads.some((r) => remaining(r.minutes) > 0);
  const histamineDue = applied !== null && remaining(histamineMinutes) === 0;
  const fullDue = applied !== null && remaining(fullMinutes) === 0;

  /** Inside the click, before any await (see the comment above). */
  function prepareCues() {
    const permission = notify === "default" ? requestNotify() : null;
    unlockAudio();
    void permission?.then(setNotify);
  }

  function track(appliedAt: number) {
    clinicTimers.start({ encounterId, organizationId, label, appliedAt, reads });
  }

  function start() {
    prepareCues();
    setBusy(true);
    void (async () => {
      try {
        const savedAt = await onStart();
        if (savedAt) track(new Date(savedAt).getTime());
      } finally {
        setBusy(false);
      }
    })();
  }

  function trackHere() {
    if (applied === null) return;
    prepareCues();
    track(applied);
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-[68ch] text-ink-secondary">Apply the tray, then start the timer. Read the histamine control at {histamineMinutes} minutes and every well at {fullMinutes} minutes. The application time is recorded with the test.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {(
          [
            ["Histamine read", histamineMinutes],
            ["All reactions", fullMinutes],
          ] as const
        ).map(([readLabel, minutes]) => {
          const ms = remaining(minutes);
          const due = applied !== null && ms === 0;
          return (
            <Card key={readLabel} dense className={due ? "border-2 border-status-warning" : undefined}>
              <p className="text-small text-ink-secondary">{readLabel}</p>
              <p className="text-h2 font-semibold tabular-nums text-anchor">{due ? "Due" : mmss(ms)}</p>
            </Card>
          );
        })}
      </div>
      <p className="sr-only" role="status">
        {fullDue ? "All reactions are due to be read." : histamineDue ? "The histamine read is due." : ""}
      </p>
      {applied ? (
        <div className="flex flex-wrap items-center gap-4">
          <Status kind="success" label={`Timer started at ${formatTime(timing.appliedAt!, timezone)}.`} />
          {!tracked && pending && (
            <Button variant="secondary" onClick={trackHere} disabled={disabled}>
              Track this timer here
            </Button>
          )}
        </div>
      ) : (
        <div>
          <Button onClick={start} disabled={disabled || busy}>
            Start timer
          </Button>
        </div>
      )}
      <NotifyNote state={notify} started={applied !== null} tracked={tracked} pending={pending} />
      {timing.readAt && <Status kind="info" label={`Reactions read at ${formatTime(timing.readAt, timezone)}.`} />}
    </div>
  );
}

/** What the browser can do for the cues, said plainly. */
function NotifyNote({ state, started, tracked, pending }: { state: NotifyState; started: boolean; tracked: boolean; pending: boolean }) {
  const elsewhere = pending
    ? "This timer was started in another tab or before the page reloaded, so this tab has no sound or timer bar for it. Choose “Track this timer here” for those (after a reload, also to stay signed in until the read), or keep this page visible to see the countdown."
    : "This timer was started in another tab or before the page reloaded.";
  const fallback = started && !tracked ? elsewhere : "Keep this tab open: the timer bar at the foot of every screen and a sound tell you when a read is due.";
  if (state === "unsupported") return <Status kind="info" label={`This browser can't show notifications. ${fallback}`} />;
  if (state === "denied") return <Status kind="info" label={`Notifications are blocked for this site (change it in the browser's site settings). ${fallback}`} />;
  if (state === "default" && !started) return <Status kind="info" label="When you start the timer, the browser asks whether to show notifications. They say only that a timer is done, never who the patient is." />;
  if (started && !tracked) return <Status kind="info" label={fallback} />;
  return null;
}
